import { bool, num, str } from './csv'
import type { Row } from './csv'
import type { QuestClassReq, QuestData, QuestDropRule, QuestDropTarget, QuestInfo, QuestObjective, QuestRule } from '../types/quest'

type QuestRows = {
  quests: Row[]
  questRules: Row[]
  questRuleClasses: Row[]
  questObjectives: Row[]
  questObjectiveClasses: Row[]
  questDropRules: Row[]
  questDropRuleTargets: Row[]
}
type QuestKey = keyof QuestRows
type Accept = <K extends string | number>(key: QuestKey, id: K | undefined, seen: Set<K>) => id is K

/**
 * class_id puede venir como código (DK, DW…) o como número de clase del GameServer
 * (0 DW, 1 DK, 2 ELF, 3 MG, 4 DL, 5 SUM, 6 RF). Ambos se normalizan al código de la guía.
 * Un valor no reconocido se conserva tal cual y aparece como "No aplica" en el panel.
 */
const CLASS_CODES: Record<string, string> = {
  '0': 'DW', DW: 'DW', WIZARD: 'DW', DARK_WIZARD: 'DW',
  '1': 'DK', DK: 'DK', KNIGHT: 'DK', DARK_KNIGHT: 'DK',
  '2': 'ELF', ELF: 'ELF', FE: 'ELF', FAIRY_ELF: 'ELF',
  '3': 'MG', MG: 'MG', MAGIC_GLADIATOR: 'MG',
  '4': 'DL', DL: 'DL', DARK_LORD: 'DL',
  '5': 'SUM', SUM: 'SUM', SU: 'SUM', SUMMONER: 'SUM',
  '6': 'RF', RF: 'RF', RAGE_FIGHTER: 'RF',
}
export const normalizeClassCode = (raw: string) => {
  const k = raw.trim().toUpperCase().replace(/\s+/g, '_')
  return CLASS_CODES[k] ?? k
}

/** Interpreta los siete archivos de misiones. Celda vacía = sin dato; "0" es un valor válido. */
export function parseQuests(rows: QuestRows, accept: Accept): QuestData {
  const quests = new Map<number, QuestInfo>()
  const seenQ = new Set<number>()
  rows.quests.forEach((r) => {
    const id = num(r.quest_id) ?? num(r.id)
    if (!accept('quests', id, seenQ)) return
    quests.set(id, { id, name: str(r.name), nameIsGenerated: bool(r.name_is_generated) })
  })

  const rules: QuestRule[] = []
  const seenR = new Set<string>()
  rows.questRules.forEach((r) => {
    const id = str(r.quest_rule_id) ?? str(r.id)
    const questId = num(r.quest_id)
    if (!accept('questRules', id, seenR) || questId === undefined) return
    rules.push({
      id,
      questId,
      state: num(r.current_state),
      stateName: str(r.current_state_name),
      npcMonsterId: num(r.npc_monster_id),
      levelMin: num(r.required_character_level_min),
      levelMax: num(r.required_character_level_max),
      requiredQuestId: num(r.required_quest_id),
      requiredQuestState: num(r.required_quest_state),
      requiredQuestStateName: str(r.required_quest_state_name),
      order: num(r.rule_order),
      startType: num(r.start_type),
    })
  })

  const classMap = (key: 'questRuleClasses' | 'questObjectiveClasses', ref: string) => {
    const out = new Map<string, QuestClassReq[]>()
    const seen = new Set<string>()
    rows[key].forEach((r) => {
      const id = str(r.id)
      const parent = str(r[ref])
      const code = str(r.class_id)
      if (!accept(key, id, seen) || !parent || !code) return
      const list = out.get(parent) ?? []
      list.push({ classCode: normalizeClassCode(code), changeUpPlusOne: num(r.required_change_up_plus_one) })
      out.set(parent, list)
    })
    return out
  }
  const ruleClasses = classMap('questRuleClasses', 'quest_rule_id')
  const objectiveClasses = classMap('questObjectiveClasses', 'quest_objective_id')

  const objectives: QuestObjective[] = []
  const seenO = new Set<string>()
  rows.questObjectives.forEach((r) => {
    const id = str(r.quest_objective_id) ?? str(r.id)
    const questId = num(r.required_quest_id)
    if (!accept('questObjectives', id, seenO) || questId === undefined) return
    objectives.push({
      id,
      questId,
      state: num(r.required_quest_state),
      stateName: str(r.required_quest_state_name),
      type: num(r.objective_type),
      typeName: str(r.objective_type_name),
      itemId: str(r.item_id),
      itemLevel: num(r.item_level),
      monsterId: num(r.monster_id),
      configuredMonsterId: num(r.configured_monster_id),
      quantity: num(r.quantity_required),
      order: num(r.objective_order),
    })
  })

  const dropRules: QuestDropRule[] = []
  const seenD = new Set<string>()
  rows.questDropRules.forEach((r) => {
    const id = str(r.quest_drop_rule_id) ?? str(r.id)
    if (!accept('questDropRules', id, seenD)) return
    dropRules.push({
      id,
      questId: num(r.quest_id),
      objectiveId: str(r.quest_objective_id),
      itemId: str(r.item_id),
      itemLevel: num(r.item_level),
      requiredState: num(r.required_quest_state),
      rollNumerator: num(r.roll_numerator),
      rollDenominator: num(r.roll_denominator),
      rollPercent: num(r.configured_roll_percent),
      selector: str(r.monster_selector),
      monsterId: num(r.monster_id),
      monsterLevelMin: num(r.monster_level_min),
      monsterLevelMax: num(r.monster_level_max),
      mapId: num(r.map_id),
      inventoryLessThan: num(r.inventory_item_count_must_be_less_than),
      quantityPerSuccess: num(r.quantity_per_success),
      probabilityScope: str(r.probability_scope),
      enabled: bool(r.enabled_by_rule),
      conditions: str(r.conditions),
    })
  })

  const dropTargets = new Map<string, QuestDropTarget[]>()
  const seenT = new Set<string>()
  rows.questDropRuleTargets.forEach((r) => {
    const id = str(r.id)
    const ruleId = str(r.quest_drop_rule_id)
    if (!accept('questDropRuleTargets', id, seenT) || !ruleId) return
    const list = dropTargets.get(ruleId) ?? []
    list.push({ id, ruleId, monsterId: num(r.monster_id), mapId: num(r.map_id) })
    dropTargets.set(ruleId, list)
  })

  const ruleIds = new Set(rules.map((r) => r.id))
  const objIds = new Set(objectives.map((o) => o.id))
  const dropIds = new Set(dropRules.map((d) => d.id))
  const orphans = [
    { label: 'quest_rules.quest_id sin misión en quests.csv', ids: rules.filter((r) => !quests.has(r.questId)).map((r) => r.id) },
    { label: 'quest_rules.required_quest_id sin misión', ids: rules.filter((r) => r.requiredQuestId !== undefined && !quests.has(r.requiredQuestId)).map((r) => r.id) },
    { label: 'quest_rule_classes sin regla', ids: Array.from(ruleClasses.keys()).filter((k) => !ruleIds.has(k)) },
    { label: 'quest_objectives.required_quest_id sin misión', ids: objectives.filter((o) => !quests.has(o.questId)).map((o) => o.id) },
    { label: 'quest_objective_classes sin objetivo', ids: Array.from(objectiveClasses.keys()).filter((k) => !objIds.has(k)) },
    { label: 'quest_drop_rules sin objetivo', ids: dropRules.filter((d) => !d.objectiveId || !objIds.has(d.objectiveId)).map((d) => d.id) },
    { label: 'quest_drop_rule_targets sin regla', ids: Array.from(dropTargets.keys()).filter((k) => !dropIds.has(k)) },
  ]

  return { quests, rules, ruleClasses, objectives, objectiveClasses, dropRules, dropTargets, orphans }
}
